import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Check,
  X
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import weeklyTimesheetService from '../../services/weeklyTimesheetService'
import employeeMasterService from '../../services/employeeMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const WeeklyTimesheetForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const timesheetData = location.state?.timesheetData

  const [formData, setFormData] = useState({
    timesheetNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    weekStartDate: '',
    weekEndDate: '',
    weekNumber: '',
    year: '',
    timeEntries: [],
    notes: '',
    status: 'Draft'
  })

  const [employees, setEmployees] = useState([])
  const [availableTimeEntries, setAvailableTimeEntries] = useState([])
  const [selectedTimeEntries, setSelectedTimeEntries] = useState([])
  const [weekInfo, setWeekInfo] = useState(null)
  const [isLoadingTimesheetNumber, setIsLoadingTimesheetNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingTimeEntries, setIsLoadingTimeEntries] = useState(false)

  // Helper function to get week start and end dates
  const getWeekDates = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    const weekStart = new Date(d.setDate(diff))
    weekStart.setHours(0, 0, 0, 0)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 6)
    weekEnd.setHours(23, 59, 59, 999)
    
    const startOfYear = new Date(weekStart.getFullYear(), 0, 1)
    const pastDaysOfYear = (weekStart - startOfYear) / 86400000
    const weekNumber = Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7)
    
    return {
      weekStartDate: weekStart.toISOString().split('T')[0],
      weekEndDate: weekEnd.toISOString().split('T')[0],
      weekNumber: weekNumber,
      year: weekStart.getFullYear()
    }
  }

  // Fetch next timesheet number (only for new timesheets)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchTimesheetNumber = async () => {
        try {
          setIsLoadingTimesheetNumber(true)
          const response = await weeklyTimesheetService.getNextTimesheetNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              timesheetNumber: response.data.timesheetNumber || response.data.formattedValue || 'WT0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching timesheet number:', error)
          setFormData(prev => ({ ...prev, timesheetNumber: 'WT0001' }))
        } finally {
          setIsLoadingTimesheetNumber(false)
        }
      }
      fetchTimesheetNumber()
    }
  }, [isEditMode, isViewMode])

  // Load employees
  useEffect(() => {
    loadEmployees()
  }, [])

  // Load timesheet data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && timesheetData?._id) {
      loadTimesheetData(timesheetData._id)
    }
  }, [isEditMode, isViewMode, timesheetData])

  // Load time entries when employee and week are selected
  useEffect(() => {
    if (formData.employee && formData.weekStartDate && !isEditMode && !isViewMode) {
      loadTimeEntriesForWeek()
    }
  }, [formData.employee, formData.weekStartDate])

  const loadEmployees = async () => {
    try {
      const response = await employeeMasterService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data || [])
      }
    } catch (error) {
      console.error('Error loading employees:', error)
    }
  }

  const loadTimeEntriesForWeek = async () => {
    if (!formData.employee || !formData.weekStartDate) return

    try {
      setIsLoadingTimeEntries(true)
      const response = await weeklyTimesheetService.getTimeEntriesForWeek(
        formData.employee,
        formData.weekStartDate
      )
      if (response.success && response.data) {
        setAvailableTimeEntries(response.data.timeEntries || [])
        setWeekInfo(response.data.weekInfo)
        // Auto-select all entries
        const entryIds = (response.data.timeEntries || []).map(entry => entry._id)
        setSelectedTimeEntries(entryIds)
      }
    } catch (error) {
      console.error('Error loading time entries:', error)
      toast.error('Failed to load time entries for the selected week')
    } finally {
      setIsLoadingTimeEntries(false)
    }
  }

  const loadTimesheetData = async (id) => {
    try {
      setIsLoading(true)
      const response = await weeklyTimesheetService.getWeeklyTimesheetById(id)
      if (response.success && response.data) {
        const timesheet = response.data
        setFormData({
          timesheetNumber: timesheet.timesheetNumber || '',
          employee: timesheet.employee?._id || timesheet.employee || '',
          employeeName: timesheet.employeeName || timesheet.employee?.employeeName || '',
          employeeCode: timesheet.employeeCode || timesheet.employee?.employeeCode || '',
          department: timesheet.department || timesheet.employee?.department || '',
          weekStartDate: timesheet.weekStartDate ? new Date(timesheet.weekStartDate).toISOString().split('T')[0] : '',
          weekEndDate: timesheet.weekEndDate ? new Date(timesheet.weekEndDate).toISOString().split('T')[0] : '',
          weekNumber: timesheet.weekNumber || '',
          year: timesheet.year || '',
          timeEntries: timesheet.timeEntries?.map(entry => entry._id || entry) || [],
          notes: timesheet.notes || '',
          status: timesheet.status || 'Draft'
        })
        setSelectedTimeEntries(timesheet.timeEntries?.map(entry => entry._id || entry) || [])
        if (timesheet.timeEntries) {
          setAvailableTimeEntries(timesheet.timeEntries)
        }
      }
    } catch (error) {
      console.error('Error loading weekly timesheet:', error)
      toast.error('Failed to load weekly timesheet data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    if (name === 'employee') {
      const selectedEmployee = employees.find(emp => emp._id === value)
      setFormData(prev => ({
        ...prev,
        employee: value,
        employeeName: selectedEmployee?.employeeName || '',
        employeeCode: selectedEmployee?.employeeCode || '',
        department: selectedEmployee?.department || ''
      }))
    } else if (name === 'weekStartDate') {
      const weekDates = getWeekDates(new Date(value))
      setFormData(prev => ({
        ...prev,
        weekStartDate: value,
        weekEndDate: weekDates.weekEndDate,
        weekNumber: weekDates.weekNumber,
        year: weekDates.year
      }))
      setWeekInfo(weekDates)
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const toggleTimeEntrySelection = (entryId) => {
    setSelectedTimeEntries(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId)
      } else {
        return [...prev, entryId]
      }
    })
  }

  const selectAllTimeEntries = () => {
    const allIds = availableTimeEntries.map(entry => entry._id)
    setSelectedTimeEntries(allIds)
  }

  const deselectAllTimeEntries = () => {
    setSelectedTimeEntries([])
  }

  const formatHours = (totalMinutes) => {
    if (!totalMinutes) return '0h 0m'
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes}m`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const calculateTotals = () => {
    const selectedEntries = availableTimeEntries.filter(entry => 
      selectedTimeEntries.includes(entry._id)
    )
    
    const totalMinutes = selectedEntries.reduce((sum, entry) => sum + (entry.totalMinutes || 0), 0)
    const billableMinutes = selectedEntries
      .filter(entry => entry.isBillable)
      .reduce((sum, entry) => sum + (entry.totalMinutes || 0), 0)
    const totalBillingAmount = selectedEntries
      .filter(entry => entry.isBillable)
      .reduce((sum, entry) => sum + (entry.billingAmount || 0), 0)
    
    return {
      totalMinutes,
      totalHours: (totalMinutes / 60).toFixed(2),
      billableMinutes,
      billableHours: (billableMinutes / 60).toFixed(2),
      totalBillingAmount
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employee || !formData.weekStartDate) {
        toast.error('Please select employee and week start date')
        return
      }
      if (selectedTimeEntries.length === 0) {
        toast.error('Please select at least one time entry')
        return
      }

      setIsLoading(true)
      
      const saveData = {
        ...formData,
        timeEntries: selectedTimeEntries
      }
      
      if (isEditMode && timesheetData?._id) {
        const response = await weeklyTimesheetService.updateWeeklyTimesheet(timesheetData._id, saveData)
        if (response.success) {
          toast.success('Weekly timesheet updated successfully!')
          navigate('/timesheets/weekly-timesheet')
        } else {
          toast.error(response.message || 'Failed to update weekly timesheet')
        }
      } else {
        const response = await weeklyTimesheetService.createWeeklyTimesheet(saveData)
        if (response.success) {
          toast.success('Weekly timesheet created successfully!')
          navigate('/timesheets/weekly-timesheet')
        } else {
          toast.error(response.message || 'Failed to create weekly timesheet')
        }
      }
    } catch (error) {
      console.error('Error saving weekly timesheet:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save weekly timesheet')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitForApproval = async () => {
    if (!formData.employee || !formData.weekStartDate || selectedTimeEntries.length === 0) {
      toast.error('Please complete all required fields and select time entries')
      return
    }

    try {
      setIsLoading(true)
      
      // First save the timesheet
      const saveData = {
        ...formData,
        timeEntries: selectedTimeEntries,
        status: 'Submitted'
      }
      
      let timesheetId = timesheetData?._id
      
      if (!timesheetId) {
        // Create new timesheet
        const createResponse = await weeklyTimesheetService.createWeeklyTimesheet(saveData)
        if (!createResponse.success) {
          toast.error(createResponse.message || 'Failed to create timesheet')
          return
        }
        timesheetId = createResponse.data._id
      } else {
        // Update existing timesheet
        const updateResponse = await weeklyTimesheetService.updateWeeklyTimesheet(timesheetId, saveData)
        if (!updateResponse.success) {
          toast.error(updateResponse.message || 'Failed to update timesheet')
          return
        }
      }
      
      // Submit for approval
      const submitResponse = await weeklyTimesheetService.submitTimesheetForApproval(timesheetId, formData.notes)
      if (submitResponse.success) {
        toast.success('Timesheet submitted for approval successfully!')
        navigate('/timesheets/weekly-timesheet')
      } else {
        toast.error(submitResponse.message || 'Failed to submit timesheet')
      }
    } catch (error) {
      console.error('Error submitting timesheet:', error)
      if (!error._errorHandled) {
        toast.error('Failed to submit timesheet for approval')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadTimesheetData(timesheetData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        weekStartDate: '',
        weekEndDate: '',
        weekNumber: '',
        year: '',
        timeEntries: [],
        notes: '',
        status: 'Draft'
      }))
      setSelectedTimeEntries([])
      setAvailableTimeEntries([])
      setWeekInfo(null)
    }
  }

  const totals = calculateTotals()
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Timesheets', path: '/timesheets' },
    { label: 'Weekly Timesheets', path: '/timesheets/weekly-timesheet' },
    { 
      label: isEditMode ? 'Edit Timesheet' : isViewMode ? 'View Timesheet' : 'New Timesheet', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading weekly timesheet...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Weekly Timesheet' : isViewMode ? 'View Weekly Timesheet' : 'New Weekly Timesheet'}</h1>
          <p>{isEditMode ? 'Update weekly timesheet details' : isViewMode ? 'View weekly timesheet details' : 'Create a new weekly timesheet'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/timesheets/weekly-timesheet')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Weekly Timesheet Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Week Details</h3></div>
          {/* Row 1: Timesheet Number, Employee, Week Start Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="timesheetNumber" className="form-label required">
                Timesheet Number
              </label>
              <input
                type="text"
                id="timesheetNumber"
                name="timesheetNumber"
                value={isLoadingTimesheetNumber ? 'Generating...' : formData.timesheetNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="employee" className="form-label required">
                Employee
              </label>
              <select
                id="employee"
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="weekStartDate" className="form-label required">
                Week Start Date
              </label>
              <input
                type="date"
                id="weekStartDate"
                name="weekStartDate"
                value={formData.weekStartDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="weekEndDate" className="form-label">
                Week End Date
              </label>
              <input
                type="date"
                id="weekEndDate"
                name="weekEndDate"
                value={formData.weekEndDate}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          </div>

          {/* Week Info */}
          {weekInfo && (
            <div style={{ 
              background: '#f8f9fa', 
              padding: '12px 16px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              fontSize: '14px',
              color: '#495057'
            }}>
              <strong>Week {weekInfo.weekNumber}</strong> of {weekInfo.year} | 
              {formData.weekStartDate && new Date(formData.weekStartDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - 
              {formData.weekEndDate && new Date(formData.weekEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          )}

          {/* Time Entries Selection */}
          {formData.employee && formData.weekStartDate && (
            <div style={{ marginBottom: '24px' }}>
              <div className="section-header">
                <h3>Time Entries</h3>
                {!isViewMode && availableTimeEntries.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={selectAllTimeEntries}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #222448',
                        borderRadius: '4px',
                        background: '#fff',
                        color: '#222448',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={deselectAllTimeEntries}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        background: '#fff',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      Deselect All
                    </button>
                  </div>
                )}
              </div>

              {isLoadingTimeEntries ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: 'var(--color-text-secondary, #5B6478)' }}>Loading time entries...</p>
                </div>
              ) : availableTimeEntries.length === 0 ? (
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '6px', 
                  padding: '16px',
                  textAlign: 'center',
                  color: '#856404'
                }}>
                  No time entries found for this week. Please create time entries first.
                </div>
              ) : (
                <div style={{ 
                  border: '1px solid #e9ecef', 
                  borderRadius: '6px', 
                  overflow: 'hidden',
                  background: '#fff'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>
                          {!isViewMode && <input
                            type="checkbox"
                            checked={selectedTimeEntries.length === availableTimeEntries.length && availableTimeEntries.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllTimeEntries()
                              } else {
                                deselectAllTimeEntries()
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />}
                          Date
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Project</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Activity</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Hours</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Billable</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableTimeEntries.map((entry) => {
                        const isSelected = selectedTimeEntries.includes(entry._id)
                        return (
                          <tr 
                            key={entry._id}
                            style={{ 
                              borderBottom: '1px solid #e9ecef',
                              cursor: isViewMode ? 'default' : 'pointer',
                              background: isSelected ? '#e7f3ff' : '#fff'
                            }}
                            onClick={() => !isViewMode && toggleTimeEntrySelection(entry._id)}
                          >
                            <td style={{ padding: '12px' }}>
                              {!isViewMode && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleTimeEntrySelection(entry._id)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ marginRight: '8px' }}
                                />
                              )}
                              {entry.entryDate ? new Date(entry.entryDate).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '12px' }}>
                              {entry.projectName || entry.project?.projectName || '-'}
                              {entry.projectCode && (
                                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{entry.projectCode}</div>
                              )}
                            </td>
                            <td style={{ padding: '12px' }}>
                              {entry.activityType || '-'}
                              {entry.activity && (
                                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{entry.activity}</div>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {formatHours(entry.totalMinutes)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {entry.isBillable ? (
                                <span style={{ color: '#28a745', fontWeight: '600' }}>Yes</span>
                              ) : (
                                <span style={{ color: 'var(--color-text-secondary, #5B6478)' }}>No</span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {entry.isBillable ? formatCurrency(entry.billingAmount, entry.currency) : '-'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Totals Summary */}
          {selectedTimeEntries.length > 0 && (
            <div style={{ 
              background: '#f8f9fa', 
              border: '1px solid #e9ecef', 
              borderRadius: '6px', 
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)', marginBottom: '16px' }}>Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Total Hours</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>{totals.totalHours}h</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Billable Hours</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#28a745' }}>{totals.billableHours}h</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Selected Entries</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>{selectedTimeEntries.length}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Billing Amount</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                    {formatCurrency(totals.totalBillingAmount)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Status (for view/edit mode) */}
          {(isEditMode || isViewMode) && (
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <input
                type="text"
                id="status"
                name="status"
                value={formData.status}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>
          )}

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading || selectedTimeEntries.length === 0}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading || selectedTimeEntries.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading || selectedTimeEntries.length === 0 ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save as Draft'}
              </button>
              {formData.status === 'Draft' && (
                <button
                  type="button"
                  onClick={handleSubmitForApproval}
                  disabled={isLoading || selectedTimeEntries.length === 0}
                  className="btn btn-success"
                  style={{
                    padding: '10px 24px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading || selectedTimeEntries.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: isLoading || selectedTimeEntries.length === 0 ? 0.6 : 1
                  }}
                >
                  <Check size={16} />
                  {isLoading ? 'Submitting...' : 'Submit for Approval'}
                </button>
              )}
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default WeeklyTimesheetForm

